/**
 * Live phone-frame PREVIEW of a mobile Home layout for the Gluon SDUI editor.
 *
 * Both screens are section-driven, so both preview the same way: each
 * `HomeSection` is laid out IN ORDER as a stylized block keyed by `section.type`,
 * conveying order + presence + copy, with schedule and visibility rules shown as
 * chips. List order is what the app renders, so it is what the preview shows.
 *
 * This is an approximation: the real app is React Native and cannot run here.
 * Icons use @mui/icons-material (gluon's installed icon set).
 */

import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import SearchRounded from '@mui/icons-material/SearchRounded'
import CardGiftcardRounded from '@mui/icons-material/CardGiftcardRounded'
import NotificationsRounded from '@mui/icons-material/NotificationsRounded'
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded'
import LocalGasStationRounded from '@mui/icons-material/LocalGasStationRounded'
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded'
import { DEFAULT_PROMO_ASPECT_RATIO, parseYouTubeVideoId, youTubeThumbnailUrl, type HomeScreenId, type HomeSection } from '@numeo/types'

const BLUE = '#2563EB'
// Accent used by main's redesigned dispatcher Home (plan badge, More link, upgrade pill).
const SCREEN_BG = '#F2F3F5'
const SLATE = '#475569'

/** Friendly labels for `visibleWhen` condition keys shown on a block's chip. */
const VISIBLE_WHEN_LABELS: Record<string, string> = {
    'plan.isFree': 'Free plan',
    'profile.incomplete': 'Setup incomplete',
    'search.limited': 'Search limited',
    'driver.isOwnerOperator': 'Owner-operator',
}

/** Safely read a string prop off a section's untyped `props` bag. */
function str(props: Record<string, unknown> | undefined, key: string): string | undefined {
    const v = props?.[key]
    return typeof v === 'string' && v.trim() ? v : undefined
}

/** A plain white content card used by most blocks. */
function Panel({ children, sx }: { children: React.ReactNode; sx?: object }) {
    return <Card sx={{ p: 1.25, gap: 0.5, borderRadius: 'md', boxShadow: 'xs', bgcolor: '#FFFFFF', ...sx }}>{children}</Card>
}

/** A small circular glyph button, e.g. a bell or gift in a header row. */
function GlyphCircle({ children, bg = '#E2E8F0', color = SLATE }: { children: React.ReactNode; bg?: string; color?: string }) {
    return (
        <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {children}
        </Box>
    )
}

/** A stubbed origin→dest chip pair for a placeholder load row. */
function LoadRow({ broker, rate, from, to }: { broker: string; rate: string; from: string; to: string }) {
    return (
        <Panel sx={{ gap: 0.75 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{broker}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#16A34A' }}>{rate}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Chip size="sm" variant="soft" color="neutral" sx={{ '--Chip-radius': '6px', fontSize: 10 }}>
                    {from}
                </Chip>
                <ChevronRightRounded sx={{ fontSize: 14, color: SLATE }} />
                <Chip size="sm" variant="soft" color="neutral" sx={{ '--Chip-radius': '6px', fontSize: 10 }}>
                    {to}
                </Chip>
            </Box>
        </Panel>
    )
}

const WORDMARK = <Typography sx={{ fontSize: 18, fontWeight: 800, color: BLUE, letterSpacing: '-0.5px' }}>numeo</Typography>

/** Render one section's stylized body, keyed by `section.type`. */
function SectionBody({ section }: { section: HomeSection }) {
    const p = section.props
    switch (section.type) {
        case 'header':
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {WORDMARK}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Chip size="sm" variant="soft" color="primary" sx={{ fontSize: 10, fontWeight: 700 }}>
                            Go
                        </Chip>
                        <GlyphCircle bg="#FEF3C7" color="#B45309">
                            <CardGiftcardRounded sx={{ fontSize: 16 }} />
                        </GlyphCircle>
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: '#7C3AED' }} />
                    </Box>
                </Box>
            )
        case 'driverHeader':
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {WORDMARK}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <GlyphCircle>
                            <NotificationsRounded sx={{ fontSize: 16 }} />
                        </GlyphCircle>
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: '#7C3AED' }} />
                    </Box>
                </Box>
            )
        case 'newEmailsBanner':
            return (
                <Sheet sx={{ p: 1.25, borderRadius: 'md', bgcolor: BLUE, color: '#FFFFFF' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>New emails</Typography>
                    <Sheet sx={{ mt: 0.75, p: 1, borderRadius: 'md', bgcolor: '#FFFFFF' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Broker reply</Typography>
                        <Typography sx={{ fontSize: 10, color: SLATE }}>Latest unread message preview</Typography>
                    </Sheet>
                </Sheet>
            )
        case 'searchCard':
            return (
                <Panel sx={{ gap: 0.75, alignItems: 'stretch' }}>
                    {['Pickup', 'Destination', 'Trailer type'].map((label) => (
                        <Sheet key={label} sx={{ px: 1, py: 0.75, borderRadius: 'sm', bgcolor: '#EBEBEC', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 11, color: SLATE, flex: 1 }}>{label}</Typography>
                            <SearchRounded sx={{ fontSize: 14, color: SLATE }} />
                        </Sheet>
                    ))}
                    <Sheet sx={{ mt: 0.25, py: 0.75, borderRadius: 'xl', bgcolor: BLUE, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#FFFFFF' }}>Show results</Typography>
                    </Sheet>
                </Panel>
            )
        case 'setupBanner':
            return (
                <Sheet sx={{ p: 1.25, borderRadius: 'md', bgcolor: '#EEF2FF', border: '1px solid #C7D2FE' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#3730A3' }}>Complete your setup</Typography>
                </Sheet>
            )
        case 'searchLimitBanner':
            return (
                <Sheet sx={{ p: 1.25, borderRadius: 'md', bgcolor: '#FEF3C7', border: '1px solid #FDE68A' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#92400E' }}>Daily search limit reached</Typography>
                </Sheet>
            )
        case 'recommendedLoads':
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Recommended Loads</Typography>
                        <Typography sx={{ fontSize: 11, color: BLUE, fontWeight: 600 }}>More</Typography>
                    </Box>
                    <LoadRow broker="Acme Logistics" rate="$2,450" from="Chicago, IL" to="Dallas, TX" />
                    <LoadRow broker="Blue Freight" rate="$1,980" from="Atlanta, GA" to="Miami, FL" />
                </Box>
            )
        case 'tutorials': {
            // Mirrors the app: a white card, "Tutorials · See all", 16:9 YouTube thumbnails in one sideways row.
            const videos = Array.isArray(p?.items) ? (p.items as { id?: string; title?: string; videoUrl?: string }[]) : []
            return (
                <Card variant="plain" sx={{ p: 1, gap: 0.75, bgcolor: '#FFFFFF', borderRadius: 12, overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Tutorials</Typography>
                        <Typography sx={{ fontSize: 10, color: BLUE, fontWeight: 500 }}>See all ›</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, overflow: 'hidden' }}>
                        {(videos.length ? videos : [{}, {}]).map((v, i) => {
                            const videoId = v.videoUrl ? parseYouTubeVideoId(v.videoUrl) : null
                            return (
                                <Box key={v.id ?? i} sx={{ width: 104, flexShrink: 0 }}>
                                    <Box
                                        sx={{
                                            aspectRatio: '16 / 9',
                                            borderRadius: 6,
                                            bgcolor: '#1E293B',
                                            backgroundImage: videoId ? `url(${youTubeThumbnailUrl(videoId)})` : undefined,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                        }}
                                    />
                                    <Typography sx={{ fontSize: 9, fontWeight: 500, mt: 0.25, lineHeight: 1.3 }} noWrap>
                                        {v.title || 'Video title'}
                                    </Typography>
                                </Box>
                            )
                        })}
                    </Box>
                </Card>
            )
        }
        case 'promoBanner': {
            // Mirrors the app: the banner IS the uploaded image, at its authored
            // aspect ratio. No image yet → a placeholder, so the operator can see
            // the campaign's position in the layout before uploading artwork.
            const imageUrl = str(p, 'imageUrl')
            const ratioProp = p?.aspectRatio
            const ratio = typeof ratioProp === 'number' && ratioProp > 0 ? ratioProp : DEFAULT_PROMO_ASPECT_RATIO
            if (!imageUrl) {
                return (
                    <Sheet
                        sx={{
                            borderRadius: 'md',
                            bgcolor: '#E2E8F0',
                            aspectRatio: String(ratio),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography sx={{ fontSize: 11, color: SLATE }}>Banner image</Typography>
                    </Sheet>
                )
            }
            return (
                <Box
                    component="img"
                    src={imageUrl}
                    alt={str(p, 'title') ?? 'Campaign banner'}
                    sx={{
                        width: '100%',
                        aspectRatio: String(ratio),
                        // `contain`, matching the app. `cover` here was actively
                        // misleading: it reproduced the device's crop faithfully, so
                        // the preview looked correct while hiding what was cut off.
                        objectFit: 'contain',
                        borderRadius: 'md',
                        display: 'block',
                        bgcolor: '#E2E8F0',
                    }}
                />
            )
        }
        case 'activeLoad':
            return (
                <Panel sx={{ alignItems: 'center', textAlign: 'center', py: 2, gap: 0.75 }}>
                    <GlyphCircle bg="#E2E8F0">
                        <Inventory2Rounded sx={{ fontSize: 16 }} />
                    </GlyphCircle>
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>No active load</Typography>
                    <Typography sx={{ fontSize: 10, color: SLATE }}>Your assigned loads will appear here</Typography>
                </Panel>
            )
        case 'fuelExpenses':
            return (
                <Panel sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GlyphCircle bg="#DCFCE7" color="#16A34A">
                            <LocalGasStationRounded sx={{ fontSize: 16 }} />
                        </GlyphCircle>
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Fuel Expenses</Typography>
                            <Typography sx={{ fontSize: 10, color: SLATE }}>Track receipts &amp; spending</Typography>
                        </Box>
                    </Box>
                    <ChevronRightRounded sx={{ fontSize: 18, color: SLATE }} />
                </Panel>
            )
        case 'loadOffers':
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Load Offers</Typography>
                    <Panel sx={{ alignItems: 'center', textAlign: 'center', py: 2 }}>
                        <Typography sx={{ fontSize: 11, color: SLATE }}>No load offers yet</Typography>
                    </Panel>
                </Box>
            )
        case 'communityFeed':
            return (
                <Panel>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Community feed</Typography>
                    <Typography sx={{ fontSize: 11, color: SLATE }}>Post composer, posts and comments</Typography>
                    <Typography sx={{ fontSize: 10, color: SLATE }}>Shown to users with Community Feed enabled</Typography>
                </Panel>
            )
        default:
            return (
                <Box sx={{ p: 1.25, borderRadius: 'md', border: '1px dashed #94A3B8', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: SLATE, fontFamily: 'monospace' }}>{section.type}</Typography>
                </Box>
            )
    }
}

/** One section: its stylized body plus small meta chips (rule / schedule / rotation). */
function PreviewSection({ section, rotationSize }: { section: HomeSection; rotationSize?: number }) {
    const ruleLabel = section.visibleWhen ? (VISIBLE_WHEN_LABELS[section.visibleWhen] ?? section.visibleWhen) : null
    const hasMeta = ruleLabel || section.schedule || section.rotationGroup
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {hasMeta && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {ruleLabel && (
                        <Chip size="sm" variant="soft" color="primary" sx={{ fontSize: 9, '--Chip-minHeight': '18px' }}>
                            shown when: {ruleLabel}
                        </Chip>
                    )}
                    {section.schedule && (
                        <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: 9, '--Chip-minHeight': '18px' }}>
                            scheduled
                        </Chip>
                    )}
                    {/* Every member of a rotation group is drawn, because the preview's
                        job is to show what was authored. On device only ONE of them is
                        on screen at a time, so the chip says so — otherwise the frame
                        reads as a promise that all three banners stack. */}
                    {section.rotationGroup && (
                        <Chip size="sm" variant="soft" color="success" sx={{ fontSize: 9, '--Chip-minHeight': '18px' }}>
                            {rotationSize && rotationSize > 1 ? `rotates 1 of ${rotationSize}: ${section.rotationGroup}` : `rotation: ${section.rotationGroup}`}
                        </Chip>
                    )}
                </Box>
            )}
            <SectionBody section={section} />
        </Box>
    )
}

/** Loads board preview: sample loads with each card after its `position`, as the app places them (`mixPromoRows`). */
function LoadsBoardPreview({ sections }: { sections: HomeSection[] }) {
    const cards = sections
        .filter((s) => s.type === 'promoCard')
        .map((s) => ({ id: s.id, props: (s.props ?? {}) as Record<string, unknown> }))
        .filter((c) => typeof c.props.position === 'number')
        .sort((a, b) => (a.props.position as number) - (b.props.position as number))
    const sampleLoads = 6
    const rows: React.ReactNode[] = []
    for (let i = 1; i <= sampleLoads; i++) {
        rows.push(<LoadRow key={`load-${i}`} broker={`Broker ${i}`} rate="$2,450" from="Chicago, IL" to="Dallas, TX" />)
        for (const card of cards.filter((c) => c.props.position === i)) {
            const tabs = Array.isArray(card.props.tabs) ? (card.props.tabs as string[]) : []
            rows.push(
                <Box key={card.id} sx={{ border: `2px solid ${BLUE}`, bgcolor: '#EEF2FF', borderRadius: '16px', p: 1, display: 'flex', gap: 0.75 }}>
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            flexShrink: 0,
                            borderRadius: '7px',
                            bgcolor: BLUE,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {typeof card.props.iconUrl === 'string' ? (
                            <Box component="img" src={card.props.iconUrl} alt="" sx={{ width: 9, height: 14, objectFit: 'contain' }} />
                        ) : null}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{String(card.props.title ?? 'Untitled card')}</Typography>
                        <Typography sx={{ fontSize: 9.5, color: SLATE }}>{String(card.props.subtitle ?? '')}</Typography>
                        {tabs.length ? <Typography sx={{ fontSize: 9, color: SLATE }}>{`only on: ${tabs.join(', ')}`}</Typography> : null}
                    </Box>
                </Box>
            )
        }
    }
    const hidden = cards.filter((c) => (c.props.position as number) > sampleLoads).length
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {rows}
            {hidden ? <Typography sx={{ fontSize: 10, color: SLATE, textAlign: 'center' }}>{`+${hidden} card(s) further down the list`}</Typography> : null}
        </Box>
    )
}

/** Banners placed next to each other render as one swipe slider on dispatcher Home, so the preview groups them too. */
type PreviewRow = { kind: 'section'; section: HomeSection } | { kind: 'slider'; sections: HomeSection[] }

function toPreviewRows(sections: HomeSection[], screen: HomeScreenId): PreviewRow[] {
    if (screen !== 'dispatcher.home') return sections.map((section) => ({ kind: 'section', section }))
    const rows: PreviewRow[] = []
    let run: HomeSection[] = []
    const flush = () => {
        if (run.length === 1) rows.push({ kind: 'section', section: run[0] })
        else if (run.length > 1) rows.push({ kind: 'slider', sections: run })
        run = []
    }
    for (const section of sections) {
        if (section.type === 'promoBanner') {
            run.push(section)
            continue
        }
        flush()
        rows.push({ kind: 'section', section })
    }
    flush()
    return rows
}

function PreviewSlider({ sections, rotationSizes }: { sections: HomeSection[]; rotationSizes: Map<string, number> }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: 9, '--Chip-minHeight': '18px', alignSelf: 'flex-start' }}>
                {`slider: ${sections.length} banners, swipe`}
            </Chip>
            <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', mx: -1.25, px: 1.25, scrollSnapType: 'x mandatory' }}>
                {sections.map((section) => (
                    <Box key={section.id} sx={{ flex: '0 0 82%', scrollSnapAlign: 'center' }}>
                        <PreviewSection section={section} rotationSize={section.rotationGroup ? rotationSizes.get(section.rotationGroup) : undefined} />
                    </Box>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                {sections.map((section, i) => (
                    <Box key={section.id} sx={{ width: 6, height: 6, borderRadius: '4px', bgcolor: i === 0 ? BLUE : '#18181B', opacity: i === 0 ? 1 : 0.15 }} />
                ))}
            </Box>
        </Box>
    )
}

interface Props {
    sections: HomeSection[]
    screen: HomeScreenId
}

/** Phone-frame preview of a Home layout draft. Updates live as the editor edits. */
export function HomeLayoutPreview({ sections, screen }: Props) {
    const visibleSections = sections.filter((section) => section.enabled !== false)
    // How many campaigns share each rotation group, so a member's chip can say
    // "1 of 3" rather than just naming the group.
    const rotationSizes = new Map<string, number>()
    for (const s of visibleSections) {
        if (!s.rotationGroup) continue
        rotationSizes.set(s.rotationGroup, (rotationSizes.get(s.rotationGroup) ?? 0) + 1)
    }
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    position: 'relative',
                    width: 300,
                    height: 620,
                    borderRadius: '40px',
                    border: '10px solid #1E293B',
                    bgcolor: SCREEN_BG,
                    overflow: 'hidden',
                    boxShadow: 'lg',
                }}
            >
                {/* Dynamic-island / notch pill */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 90,
                        height: 22,
                        borderRadius: '12px',
                        bgcolor: '#0F172A',
                        zIndex: 2,
                    }}
                />
                {/* Scrollable screen content */}
                <Box
                    sx={{ position: 'absolute', inset: 0, pt: '44px', px: 1.25, pb: 1.25, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                    {/* Both screens preview in LIST ORDER, because that is exactly how
                        the app renders them. The dispatcher frame used to draw fixed
                        native chrome and bucket campaigns by `props.placement`, which
                        made the preview disagree with the device once dispatcher Home
                        became section-driven — a promo could preview below Recommended
                        and ship above the header. */}
                    {screen === 'dispatcher.loads' ? (
                        <LoadsBoardPreview sections={visibleSections} />
                    ) : visibleSections.length === 0 ? (
                        <Box sx={{ mt: 6, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 12, color: SLATE }}>No sections enabled</Typography>
                        </Box>
                    ) : (
                        toPreviewRows(visibleSections, screen).map((row) =>
                            row.kind === 'slider' ? (
                                <PreviewSlider key={`slider:${row.sections[0].id}`} sections={row.sections} rotationSizes={rotationSizes} />
                            ) : (
                                <PreviewSection
                                    key={row.section.id}
                                    section={row.section}
                                    rotationSize={row.section.rotationGroup ? rotationSizes.get(row.section.rotationGroup) : undefined}
                                />
                            )
                        )
                    )}
                </Box>
            </Box>
            <Typography level="body-xs" color="neutral" sx={{ maxWidth: 300, textAlign: 'center' }}>
                {`Live preview of the draft, in the order the app renders it — actual rendering is native; conditional sections are shown with their rule. (${screen})`}
            </Typography>
        </Box>
    )
}
